/* eslint-disable max-lines */

import cloneDeep from 'lodash/cloneDeep';
import omit from 'lodash/omit';
import { defineStore } from 'pinia';

import { getPersistingErrorActions } from '@evidentid/dashboard-commons/modules/persisting-error';
import { getSnackbarActions } from '@evidentid/dashboard-commons/modules/snackbar';
import { GatewayApiClient } from '@evidentid/gateway-api-client';
import {
    OperationError,
} from '@evidentid/rpweb-api-client/models';
import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';
import { EntityRiskProfile, EffectiveRiskProfiles } from '@evidentid/tprm-portal-lib/models/decisioning';
import {
    Criterion,
    CriterionInput,
    CriterionMessage,
    CriterionTemplate,
} from '@evidentid/tprm-portal-lib/models/decisioning/Criterion.model';
import {
    RiskProfile, RiskProfileInput,
} from '@evidentid/tprm-portal-lib/models/decisioning/RiskProfile.model';
import {
    TprmRequirementModel,
} from '@evidentid/tprm-portal-lib/models/entity-details/TprmRequirementModel.model';
import { RequirementTypesPerCountry } from '@evidentid/tprm-portal-lib/models/entity-details/TprmRequirementType';
import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

export interface DecisioningCriteriaRequirement {
    gatewayClient: GatewayApiClient;
}

// Actions reach the Application through the $app Pinia plugin; typed here once.
function appOf(store: { $app: any }): DecisioningCriteriaRequirement {
    return store.$app;
}

export interface RiskProfilesStatus {
    status: OperationStatus;
    list: RiskProfile[];
}

export interface EntityRiskProfilesStatus {
    status: OperationStatus;
    list: EntityRiskProfile[];
}

export type EntityRiskProfilesStatusPerEntity = Record<string, EntityRiskProfilesStatus>;

export interface RiskProfileStatus {
    status: OperationStatus;
    data: RiskProfile | null;
}

export interface CriteriaStatus {
    status: OperationStatus;
    criteria: Record<string, Criterion[]>;
}

export interface CriterionMessageStatus {
    status: OperationStatus;
    data: CriterionMessage | null;
    latestCallNum?: number | null;
}

export type CriterionMessageStatusMap = Record<string, CriterionMessageStatus>;

export interface CriterionTemplatesStatus {
    status: OperationStatus;
    list: CriterionTemplate[];
}

export interface RiskProfileCopyDraftStatus {
    status: OperationStatus;
    sourceRiskProfileId: string | null;
    riskProfile: RiskProfileInput | null;
    criteria: CriterionInput[];
}

export interface RequirementTypesPerCountryStatus {
    status: OperationStatus;
    data: RequirementTypesPerCountry;
}

export interface CustomPropertiesListStatus {
    status: OperationStatus;
    list: CustomProperty[];
}

export interface EntityEffectiveRiskProfileStatus {
    status: OperationStatus;
    list: EffectiveRiskProfiles[];
}

export interface RequirementModelsStatus {
    status: OperationStatus;
    list: TprmRequirementModel[];
}

interface DecisioningCriteriaState {
    customProperties: Record<string, CustomPropertiesListStatus>;
    riskProfiles: Record<string, RiskProfilesStatus>;
    entityRiskProfiles: Record<string, EntityRiskProfilesStatusPerEntity>;
    currentRiskProfile: Record<string, RiskProfileStatus>;
    criteria: Record<string, CriteriaStatus>;
    criterionTemplates: Record<string, CriterionTemplatesStatus>;
    copyRiskProfileDraft: Record<string, RiskProfileCopyDraftStatus>;
    criterionMessage: Record<string, CriterionMessageStatusMap>;
    requirementTypesPerCountry: Record<string, RequirementTypesPerCountryStatus>;
    deletingRiskProfileStatus: Record<string, OperationStatus>;
    creatingRiskProfileStatus: Record<string, OperationStatus>;
    creatingCriteriaStatus: Record<string, OperationStatus>;
    patchingCriteriaStatus: Record<string, OperationStatus>;
    updatingRiskProfileStatus: Record<string, OperationStatus>;
    effectiveRiskProfileStatus: Record<string, EntityEffectiveRiskProfileStatus>;
    requirementModelsStatus: Record<string, RequirementModelsStatus>;
}

export function createEmptyRiskProfilesStatus(): RiskProfilesStatus {
    return {
        status: OperationStatus.uninitialized,
        list: [],
    };
}

export function createEmptyCurrentRiskProfileStatus(): RiskProfileStatus {
    return {
        status: OperationStatus.uninitialized,
        data: null,
    };
}

export function createEmptyCustomPropertyStatus(): CustomPropertiesListStatus {
    return {
        status: OperationStatus.uninitialized,
        list: [],
    };
}

export function createEmptyCriterionTemplatesStatus(): CriterionTemplatesStatus {
    return {
        status: OperationStatus.uninitialized,
        list: [],
    };
}

export function createEmptyRiskProfileCopyDraftStatus(): RiskProfileCopyDraftStatus {
    return {
        status: OperationStatus.uninitialized,
        sourceRiskProfileId: null,
        riskProfile: null,
        criteria: [],
    };
}

export function createEmptyCriteriaStatus(): CriteriaStatus {
    return {
        status: OperationStatus.uninitialized,
        criteria: {},
    };
}

export function createEmptyRequirementTypesPerCountry(): RequirementTypesPerCountryStatus {
    return {
        status: OperationStatus.uninitialized,
        data: {},
    };
}

export function createEmptyEffectiveRiskProfileStatus(): EntityEffectiveRiskProfileStatus {
    return {
        status: OperationStatus.uninitialized,
        list: [],
    };
}

export function createEmptyRequirementModelsStatus(): RequirementModelsStatus {
    return {
        status: OperationStatus.uninitialized,
        list: [],
    };
}

const handleActionError = async (scopedContext: { $app: any }, mutationCall: Function, data: any, error: string) => {
    mutationCall(data);
    const notFoundErrorPattern = /^Error: The requested resource is not found.?/;
    const errorType = notFoundErrorPattern.test(error) ? 'page-not-found' : error;
    await getPersistingErrorActions().showError(errorType);
};

function createRiskProfileCopyDraft(riskProfile: RiskProfile): {
    sourceRiskProfileId: string;
    copiedRiskProfile: RiskProfileInput;
} {
    const clonedRiskProfile = cloneDeep(riskProfile);
    const { id: sourceRiskProfileId, displayName, ...copiedRiskProfile } = clonedRiskProfile;
    return {
        sourceRiskProfileId,
        copiedRiskProfile: {
            ...copiedRiskProfile,
            active: false,
            displayName: `${displayName} - COPY`,
        },
    };
}

function createCriterionCopyDraft(criterion: Criterion): CriterionInput {
    const clonedCriterion = cloneDeep(criterion) as Criterion & Partial<CriterionInput>;
    const copiedCriterion: CriterionInput = {
        coverageType: clonedCriterion.coverageType,
        field: clonedCriterion.field,
        evaluator: clonedCriterion.evaluator,
        verify: clonedCriterion.verify,
        emailMessage: clonedCriterion.emailMessage,
        deprecated: clonedCriterion.deprecated,
    };
    if (clonedCriterion.displayMetadata !== undefined) {
        copiedCriterion.displayMetadata = clonedCriterion.displayMetadata;
    }
    return copiedCriterion;
}

export const useDecisioningCriteriaStore = defineStore('decisioningCriteria', {
    state: (): DecisioningCriteriaState => ({
        customProperties: {},
        riskProfiles: {},
        entityRiskProfiles: {},
        criteria: {},
        copyRiskProfileDraft: {},
        criterionMessage: {},
        deletingRiskProfileStatus: {},
        creatingRiskProfileStatus: {},
        creatingCriteriaStatus: {},
        patchingCriteriaStatus: {},
        updatingRiskProfileStatus: {},
        currentRiskProfile: {},
        criterionTemplates: {},
        requirementTypesPerCountry: {},
        effectiveRiskProfileStatus: {},
        requirementModelsStatus: {},
    }),
    actions: {
        startLoadingRiskProfiles(payload: { rpName: string }) {
            this.riskProfiles = {
                ...this.riskProfiles,
                [payload.rpName]: {
                    ...createEmptyRiskProfilesStatus(),
                    ...(this.riskProfiles[payload.rpName] as RiskProfilesStatus),
                    status: OperationStatus.loading,
                },
            };
        },
        finishLoadingRiskProfiles(
            payload: { rpName: string, riskProfiles: RiskProfile[] },
        ) {
            this.riskProfiles = {
                ...this.riskProfiles,
                [payload.rpName]: {
                    ...(this.riskProfiles[payload.rpName] as RiskProfilesStatus),
                    status: OperationStatus.success,
                    list: payload.riskProfiles,
                },
            };
        },
        failLoadingRiskProfiles(payload: { rpName: string }) {
            this.riskProfiles = {
                ...this.riskProfiles,
                [payload.rpName]: {
                    ...(this.riskProfiles[payload.rpName] as RiskProfilesStatus),
                    status: OperationStatus.error,
                    list: [],
                },
            };
        },
        startLoadingEntityRiskProfiles(payload: { rpName: string, entityId: string }) {
            this.entityRiskProfiles = {
                ...this.entityRiskProfiles,
                [payload.rpName]: {
                    ...this.entityRiskProfiles[payload.rpName],
                    [payload.entityId]: {
                        status: OperationStatus.loading,
                        list: [],
                    },
                },
            };
        },
        finishLoadingEntityRiskProfiles(
            payload: { rpName: string, entityId: string, entityRiskProfiles: EntityRiskProfile[] },
        ) {
            this.entityRiskProfiles = {
                ...this.entityRiskProfiles,
                [payload.rpName]: {
                    ...this.entityRiskProfiles[payload.rpName],
                    [payload.entityId]: {
                        status: OperationStatus.success,
                        list: payload.entityRiskProfiles,
                    },
                },
            };
        },
        failLoadingEntityRiskProfiles(payload: { rpName: string, entityId: string }) {
            this.entityRiskProfiles = {
                ...this.entityRiskProfiles,
                [payload.rpName]: {
                    ...this.entityRiskProfiles[payload.rpName],
                    [payload.entityId]: {
                        status: OperationStatus.error,
                        list: [],
                    },
                },
            };
        },
        startLoadingCurrentRiskProfile(payload: { rpName: string }) {
            this.currentRiskProfile = {
                ...this.currentRiskProfile,
                [payload.rpName]: {
                    ...createEmptyCurrentRiskProfileStatus(),
                    ...(this.currentRiskProfile[payload.rpName] as RiskProfileStatus),
                    status: OperationStatus.loading,
                },
            };
        },
        finishLoadingCurrentRiskProfile(
            payload: { rpName: string, riskProfile: RiskProfile },
        ) {
            this.currentRiskProfile = {
                ...this.currentRiskProfile,
                [payload.rpName]: {
                    ...(this.currentRiskProfile[payload.rpName] as RiskProfileStatus),
                    status: OperationStatus.success,
                    data: payload.riskProfile,
                },
            };
        },
        failLoadingCurrentRiskProfile(payload: { rpName: string }) {
            this.currentRiskProfile = {
                ...this.currentRiskProfile,
                [payload.rpName]: {
                    ...(this.currentRiskProfile[payload.rpName] as RiskProfileStatus),
                    status: OperationStatus.error,
                    data: null,
                },
            };
        },
        startLoadingCriteria(payload: { rpName: string }) {
            this.criteria = {
                ...this.criteria,
                [payload.rpName]: {
                    ...createEmptyCriteriaStatus(),
                    ...(this.criteria[payload.rpName] as CriteriaStatus),
                    status: OperationStatus.loading,
                },
            };
        },
        finishLoadingCriteria(
            payload: { rpName: string, riskProfileId: string, criteria: Criterion[] },
        ) {
            this.criteria = {
                ...this.criteria,
                [payload.rpName]: {
                    ...(this.criteria[payload.rpName] as CriteriaStatus),
                    status: OperationStatus.success,
                    criteria: {
                        ...(this.criteria[payload.rpName]?.criteria || {}),
                        [payload.riskProfileId]: payload.criteria,
                    },
                },
            };
        },
        failLoadingCriteria(payload: { rpName: string }) {
            this.criteria = {
                ...this.criteria,
                [payload.rpName]: {
                    ...(this.criteria[payload.rpName] as CriteriaStatus),
                    status: OperationStatus.error,
                    criteria: {
                        ...(this.criteria[payload.rpName]?.criteria || {}),
                    },
                },
            };
        },
        startLoadingCriterionMessage(
            payload: { rpName: string, criterionId: string, latestCallNum?: number | null },
        ) {
            const currentState = this.criterionMessage[payload.rpName]
                ? this.criterionMessage[payload.rpName][payload.criterionId]
                : null;
            this.criterionMessage = {
                ...this.criterionMessage,
                [payload.rpName]: {
                    ...this.criterionMessage[payload.rpName],
                    [payload.criterionId]: {
                        status: OperationStatus.loading,
                        data: currentState?.data || null,
                        latestCallNum: payload.latestCallNum || null,
                    },
                },
            };
        },
        finishLoadingCriterionMessage(
            payload: { rpName: string, criterionId: string, criterionMessage: CriterionMessage },
        ) {
            this.criterionMessage = {
                ...this.criterionMessage,
                [payload.rpName]: {
                    ...this.criterionMessage[payload.rpName],
                    [payload.criterionId]: {
                        status: OperationStatus.success,
                        data: payload.criterionMessage,
                        latestCallNum: null,
                    },
                },
            };
        },
        failLoadingCriterionMessage(payload: { rpName: string, criterionId: string }) {
            const currentState = this.criterionMessage[payload.rpName]
                ? this.criterionMessage[payload.rpName][payload.criterionId]
                : null;
            this.criterionMessage = {
                ...this.criterionMessage,
                [payload.rpName]: {
                    ...this.criterionMessage[payload.rpName],
                    [payload.criterionId]: {
                        status: OperationStatus.error,
                        data: currentState?.data || null,
                        latestCallNum: null,
                    },
                },
            };
        },
        startLoadingCriterionTemplates(payload: { rpName: string }) {
            this.criterionTemplates = {
                ...this.criterionTemplates,
                [payload.rpName]: {
                    ...createEmptyCriterionTemplatesStatus(),
                    ...(this.criterionTemplates[payload.rpName] as CriterionTemplatesStatus),
                    status: OperationStatus.loading,
                },
            };
        },
        finishLoadingCriterionTemplates(
            payload: { rpName: string, templates: CriterionTemplate[] },
        ) {
            this.criterionTemplates = {
                ...this.criterionTemplates,
                [payload.rpName]: {
                    ...(this.criterionTemplates[payload.rpName] as CriterionTemplatesStatus),
                    status: OperationStatus.success,
                    list: payload.templates,
                },
            };
        },
        startPreparingRiskProfileCopy(payload: { rpName: string }) {
            this.copyRiskProfileDraft = {
                ...this.copyRiskProfileDraft,
                [payload.rpName]: {
                    ...createEmptyRiskProfileCopyDraftStatus(),
                    ...(this.copyRiskProfileDraft[payload.rpName] as RiskProfileCopyDraftStatus),
                    status: OperationStatus.loading,
                },
            };
        },
        finishPreparingRiskProfileCopy(payload: {
            rpName: string;
            sourceRiskProfileId: string;
            riskProfile: RiskProfileInput;
            criteria: CriterionInput[];
        }) {
            this.copyRiskProfileDraft = {
                ...this.copyRiskProfileDraft,
                [payload.rpName]: {
                    status: OperationStatus.success,
                    sourceRiskProfileId: payload.sourceRiskProfileId,
                    riskProfile: payload.riskProfile,
                    criteria: payload.criteria,
                },
            };
        },
        failPreparingRiskProfileCopy(payload: { rpName: string }) {
            this.copyRiskProfileDraft = {
                ...this.copyRiskProfileDraft,
                [payload.rpName]: {
                    ...createEmptyRiskProfileCopyDraftStatus(),
                    status: OperationStatus.error,
                },
            };
        },
        failLoadingCriterionTemplates(payload: { rpName: string }) {
            this.criterionTemplates = {
                ...this.criterionTemplates,
                [payload.rpName]: {
                    ...(this.criterionTemplates[payload.rpName] as CriterionTemplatesStatus),
                    status: OperationStatus.error,
                    list: [],
                },
            };
        },
        startLoadingRequirementTypesPerCountry(payload: { rpName: string }) {
            this.requirementTypesPerCountry = {
                ...this.requirementTypesPerCountry,
                [payload.rpName]: {
                    ...createEmptyRequirementTypesPerCountry(),
                    ...this.requirementTypesPerCountry[payload.rpName],
                    status: OperationStatus.loading,
                },
            };
        },
        finishLoadingRequirementTypesPerCountry(
            payload: { rpName: string, requirementTypesPerCountry: RequirementTypesPerCountry },
        ) {
            this.requirementTypesPerCountry = {
                ...this.requirementTypesPerCountry,
                [payload.rpName]: {
                    ...this.requirementTypesPerCountry[payload.rpName],
                    status: OperationStatus.success,
                    data: payload.requirementTypesPerCountry,
                },
            };
        },
        failLoadingRequirementTypesPerCountry(payload: { rpName: string }) {
            this.requirementTypesPerCountry = {
                ...this.requirementTypesPerCountry,
                [payload.rpName]: {
                    ...this.requirementTypesPerCountry[payload.rpName],
                    status: OperationStatus.error,
                    data: {},
                },
            };
        },
        startDeletingRiskProfile(payload: { rpName: string }) {
            this.deletingRiskProfileStatus = {
                ...this.deletingRiskProfileStatus,
                [payload.rpName]: OperationStatus.loading,
            };
        },
        finishDeletingRiskProfile(
            payload: { rpName: string, riskProfileId: string },
        ) {
            this.deletingRiskProfileStatus = {
                ...this.deletingRiskProfileStatus,
                [payload.rpName]: OperationStatus.success,
            };
            const riskProfiles = this.riskProfiles[payload.rpName]?.list || [];
            const deletedIndex = riskProfiles.findIndex((riskProfile) => riskProfile.id === payload.riskProfileId);
            if (deletedIndex >= 0) {
                const newList = [ ...riskProfiles.slice(0, deletedIndex), ...riskProfiles.slice(deletedIndex + 1) ];
                this.riskProfiles = {
                    ...this.riskProfiles,
                    [payload.rpName]: {
                        ...(this.riskProfiles[payload.rpName] as RiskProfilesStatus),
                        list: newList,
                    },
                };
            }
        },
        failDeletingRiskProfile(payload: { rpName: string }) {
            this.deletingRiskProfileStatus = {
                ...this.deletingRiskProfileStatus,
                [payload.rpName]: OperationStatus.error,
            };
        },
        startCreatingRiskProfile(payload: { rpName: string }) {
            this.creatingRiskProfileStatus = {
                ...this.creatingRiskProfileStatus,
                [payload.rpName]: OperationStatus.loading,
            };
        },
        finishCreatingRiskProfile(
            payload: { rpName: string, riskProfile: RiskProfile },
        ) {
            this.creatingRiskProfileStatus = {
                ...this.creatingRiskProfileStatus,
                [payload.rpName]: OperationStatus.success,
            };
            this.currentRiskProfile = {
                ...this.currentRiskProfile,
                [payload.rpName]: {
                    ...(this.currentRiskProfile[payload.rpName] as RiskProfileStatus),
                    data: payload.riskProfile,
                },
            };
        },
        failCreatingRiskProfile(payload: { rpName: string }) {
            this.creatingRiskProfileStatus = {
                ...this.creatingRiskProfileStatus,
                [payload.rpName]: OperationStatus.error,
            };
        },
        startCreatingCriteria(payload: { rpName: string }) {
            this.creatingCriteriaStatus = {
                ...this.creatingCriteriaStatus,
                [payload.rpName]: OperationStatus.loading,
            };
        },
        finishCreatingCriteria(
            payload: { rpName: string },
        ) {
            this.creatingCriteriaStatus = {
                ...this.creatingCriteriaStatus,
                [payload.rpName]: OperationStatus.success,
            };
        },
        failCreatingCriteria(payload: { rpName: string }) {
            this.creatingCriteriaStatus = {
                ...this.creatingCriteriaStatus,
                [payload.rpName]: OperationStatus.error,
            };
        },
        startPatchingCriteria(payload: { rpName: string }) {
            this.patchingCriteriaStatus = {
                ...this.patchingCriteriaStatus,
                [payload.rpName]: OperationStatus.loading,
            };
        },
        finishPatchingCriteria(
            payload: { rpName: string },
        ) {
            this.patchingCriteriaStatus = {
                ...this.patchingCriteriaStatus,
                [payload.rpName]: OperationStatus.success,
            };
        },
        failPatchingCriteria(payload: { rpName: string }) {
            this.patchingCriteriaStatus = {
                ...this.patchingCriteriaStatus,
                [payload.rpName]: OperationStatus.error,
            };
        },
        startUpdatingRiskProfile(payload: { rpName: string }) {
            this.updatingRiskProfileStatus = {
                ...this.updatingRiskProfileStatus,
                [payload.rpName]: OperationStatus.loading,
            };
        },
        finishUpdatingRiskProfile(
            payload: { rpName: string },
        ) {
            this.updatingRiskProfileStatus = {
                ...this.updatingRiskProfileStatus,
                [payload.rpName]: OperationStatus.success,
            };
        },
        failUpdatingRiskProfile(payload: { rpName: string }) {
            this.updatingRiskProfileStatus = {
                ...this.updatingRiskProfileStatus,
                [payload.rpName]: OperationStatus.error,
            };
        },
        startLoadingEffectiveRiskProfileStatus(payload: { rpName: string }) {
            this.effectiveRiskProfileStatus = {
                ...this.effectiveRiskProfileStatus,
                [payload.rpName]: {
                    status: OperationStatus.loading,
                    list: [],
                },
            };
        },
        finishLoadingEffectiveRiskProfileStatus(
            payload: { rpName: string, list: EffectiveRiskProfiles[] },
        ) {
            this.effectiveRiskProfileStatus = {
                ...this.effectiveRiskProfileStatus,
                [payload.rpName]: {
                    status: OperationStatus.success,
                    list: payload.list,
                },
            };
        },
        failLoadingEffectiveRiskProfileStatus(payload: { rpName: string }) {
            this.effectiveRiskProfileStatus = {
                ...this.effectiveRiskProfileStatus,
                [payload.rpName]: {
                    status: OperationStatus.error,
                    list: [],
                },
            };
        },
        startLoadingRequirementModels(payload: { rpName: string }) {
            this.requirementModelsStatus = {
                ...this.requirementModelsStatus,
                [payload.rpName]: {
                    status: OperationStatus.loading,
                    list: [],
                },
            };
        },
        finishLoadingRequirementModels(
            payload: { rpName: string, list: TprmRequirementModel[] },
        ) {
            this.requirementModelsStatus = {
                ...this.requirementModelsStatus,
                [payload.rpName]: {
                    status: OperationStatus.success,
                    list: payload.list,
                },
            };
        },
        failLoadingRequirementModels(payload: { rpName: string }) {
            this.requirementModelsStatus = {
                ...this.requirementModelsStatus,
                [payload.rpName]: {
                    status: OperationStatus.error,
                    list: [],
                },
            };
        },
        clearEntityRiskProfilesPerEntity(payload: { rpName: string, entityId: string }) {
            const { rpName, entityId } = payload;
            this.entityRiskProfiles = {
                ...this.entityRiskProfiles,
                [rpName]: {
                    ...omit(this.entityRiskProfiles[rpName], entityId),
                },
            };
        },
        clearEntityRiskProfilesPerRp(rpName: string) {
            this.entityRiskProfiles = omit(this.entityRiskProfiles, [ rpName ]);
        },
        clearCurrentRiskProfile(rpName: string) {
            this.currentRiskProfile = omit(this.currentRiskProfile, [ rpName ]);
        },
        clearEffectiveRiskProfileStatus(rpName: string) {
            this.effectiveRiskProfileStatus = omit(this.effectiveRiskProfileStatus, [ rpName ]);
        },
        async loadRiskProfiles(payload: {
            rpName: string;
        }) {
            const { rpName } = payload;
            const currentStatus =
                this.riskProfiles[rpName] || createEmptyRiskProfilesStatus();
            if (currentStatus.status === OperationStatus.loading) {
                return;
            }
            this.startLoadingRiskProfiles({ rpName });
            try {
                const latestStatus =
                    this.riskProfiles[rpName] || createEmptyRiskProfilesStatus();
                if (latestStatus.status !== OperationStatus.loading &&
                latestStatus.status !== OperationStatus.uninitialized) {
                    return;
                }
                const riskProfiles = await appOf(this).gatewayClient.tprmUi().getTprmRiskProfiles(rpName);
                this.finishLoadingRiskProfiles({
                    rpName,
                    riskProfiles,
                });
            } catch (error) {
                this.failLoadingRiskProfiles({ rpName });
                await getPersistingErrorActions().showError(error);
            }
        },
        async loadEntityRiskProfiles(payload: { rpName: string, entityId: string }) {
            const { rpName, entityId } = payload;
            const currentRpStatus = this.entityRiskProfiles[rpName] || {};
            const currentStatus = currentRpStatus[entityId] || { status: OperationStatus.uninitialized, list: [] };
            if (currentStatus.status === OperationStatus.loading) {
                return;
            }
            this.startLoadingEntityRiskProfiles({ rpName, entityId });
            try {
                const latestRpStatus = this.entityRiskProfiles[rpName] || {};
                const latestStatus =
                    latestRpStatus[entityId] || { status: OperationStatus.uninitialized, list: [] };
                if (latestStatus.status !== OperationStatus.loading &&
                latestStatus.status !== OperationStatus.uninitialized) {
                    return;
                }
                const entityRiskProfiles = await appOf(this).gatewayClient.tprmUi()
                    .getTprmEntityRiskProfiles(rpName, entityId);
                this.finishLoadingEntityRiskProfiles({
                    rpName,
                    entityId,
                    entityRiskProfiles,
                });
            } catch (error) {
                this.failLoadingEntityRiskProfiles({ rpName, entityId });
                await getPersistingErrorActions().showError(error);
            }
        },
        async loadRiskProfile(payload: { rpName: string, id: string }) {
            const { rpName, id } = payload;
            const currentStatus =
                this.currentRiskProfile[rpName] || createEmptyCurrentRiskProfileStatus();
            if (currentStatus.status === OperationStatus.loading) {
                return;
            }
            this.startLoadingCurrentRiskProfile({ rpName });
            try {
                const latestStatus =
                    this.currentRiskProfile[rpName] || createEmptyCurrentRiskProfileStatus();
                if (latestStatus.status !== OperationStatus.loading &&
                latestStatus.status !== OperationStatus.uninitialized) {
                    return;
                }
                const riskProfile = await appOf(this).gatewayClient.tprmUi().getTprmRiskProfile(rpName, id);
                this.finishLoadingCurrentRiskProfile({ rpName, riskProfile });
            } catch (error) {
                await handleActionError(this, this.failLoadingCurrentRiskProfile, { rpName }, error);
            }
        },
        async loadCriteria(payload: { rpName: string, riskProfileId: string }) {
            const { rpName, riskProfileId } = payload;
            const currentStatus =
                this.criteria[rpName] || createEmptyCriteriaStatus();
            if (currentStatus.status === OperationStatus.loading) {
                return;
            }
            this.startLoadingCriteria({ rpName });
            try {
                const latestStatus =
                    this.criteria[rpName] || createEmptyCriteriaStatus();
                if (latestStatus.status !== OperationStatus.loading &&
                latestStatus.status !== OperationStatus.uninitialized) {
                    return;
                }
                const criteria = await appOf(this).gatewayClient.tprmUi().getTprmCriteria(rpName, riskProfileId);
                this.finishLoadingCriteria({ rpName, riskProfileId, criteria });
            } catch (error) {
                await handleActionError(this, this.failLoadingCriteria, { rpName }, error);
            }
        },
        async loadCriterionTemplates(payload: { rpName: string, countryCode: string }) {
            const { rpName, countryCode } = payload;
            const currentStatus =
                this.criterionTemplates[rpName] || createEmptyCriterionTemplatesStatus();
            if (currentStatus.status === OperationStatus.loading) {
                return;
            }
            this.startLoadingCriterionTemplates({ rpName });
            try {
                const latestStatus =
                    this.criterionTemplates[rpName] || createEmptyCriterionTemplatesStatus();
                if (latestStatus.status !== OperationStatus.loading &&
                latestStatus.status !== OperationStatus.uninitialized) {
                    return;
                }
                const templates = await appOf(this).gatewayClient.tprmUi()
                    .getTprmCriteriaTemplatesPerCountry(rpName, countryCode);
                this.finishLoadingCriterionTemplates({
                    rpName,
                    templates,
                });
            } catch (error) {
                this.failLoadingCriterionTemplates({ rpName });
                await getPersistingErrorActions().showError(error);
            }
        },
        async prepareRiskProfileCopy(payload: { rpName: string, riskProfile: RiskProfile }) {
            const { rpName, riskProfile } = payload;
            const currentStatus =
                this.copyRiskProfileDraft[rpName] || createEmptyRiskProfileCopyDraftStatus();
            if (currentStatus.status === OperationStatus.loading) {
                return;
            }
            this.startPreparingRiskProfileCopy({ rpName });
            try {
                const latestStatus =
                    this.copyRiskProfileDraft[rpName] || createEmptyRiskProfileCopyDraftStatus();
                if (latestStatus.status !== OperationStatus.loading &&
                latestStatus.status !== OperationStatus.uninitialized) {
                    return;
                }
                const { sourceRiskProfileId, copiedRiskProfile } = createRiskProfileCopyDraft(riskProfile);
                const copiedCriteria = await appOf(this).gatewayClient.tprmUi()
                    .getTprmCriteria(rpName, sourceRiskProfileId);
                this.finishPreparingRiskProfileCopy({
                    rpName,
                    sourceRiskProfileId,
                    riskProfile: copiedRiskProfile,
                    criteria: copiedCriteria.map(createCriterionCopyDraft),
                });
            } catch (error) {
                this.failPreparingRiskProfileCopy({ rpName });
                await getPersistingErrorActions().showError(error);
            }
        },
        async loadCriterionMessage(
            payload: {
                rpName: string;
                criterion: CriterionInput | Criterion;
                countryCode: string;
            },
        ) {
            const { rpName, criterion, countryCode } = payload;
            const criterionId = 'id' in criterion ? criterion.id : `${criterion.coverageType}-${criterion.field}`;
            const currentState = this.criterionMessage?.[rpName]?.[criterionId] ||
            { status: OperationStatus.uninitialized, data: null };
            const localNum = (currentState.latestCallNum ?? 0) + 1;
            this.startLoadingCriterionMessage({ rpName, criterionId, latestCallNum: localNum });
            try {
                const criterionMessage = await appOf(this).gatewayClient.tprmUi()
                    .postTprmCriterionForMessage(criterion, countryCode);
                const latestState = this.criterionMessage?.[rpName]?.[criterionId] ||
                { status: OperationStatus.uninitialized, data: null };
                if (latestState.latestCallNum === localNum) {
                    this.finishLoadingCriterionMessage({ rpName, criterionId, criterionMessage });
                }
            } catch (error) {
                this.failLoadingCriterionMessage({ rpName, criterionId });
                console.error(error);
            }
        },
        async loadRequirementTypesPerCountry(payload: { rpName: string }) {
            const { rpName } = payload;
            const currentStatus =
                this.requirementTypesPerCountry[rpName] || createEmptyRequirementTypesPerCountry();
            if (currentStatus.status === OperationStatus.loading) {
                return;
            }
            this.startLoadingRequirementTypesPerCountry({ rpName });
            try {
                const latestStatus =
                    this.requirementTypesPerCountry[rpName] || createEmptyRequirementTypesPerCountry();
                if (latestStatus.status !== OperationStatus.loading &&
                latestStatus.status !== OperationStatus.uninitialized) {
                    return;
                }
                const requirementTypesPerCountry = await appOf(this).gatewayClient.tprmUi()
                    .getRequirementTypesPerCountry();
                this.finishLoadingRequirementTypesPerCountry({ rpName, requirementTypesPerCountry });
            } catch (error) {
                this.failLoadingRequirementTypesPerCountry({ rpName });
                await getPersistingErrorActions().showError(error);
            }
        },
        async deleteRiskProfile(payload: { rpName: string, riskProfileId: string }) {
            const { rpName, riskProfileId } = payload;
            const currentStatus =
                this.deletingRiskProfileStatus[rpName] || OperationStatus.uninitialized;
            if (currentStatus === OperationStatus.loading) {
                return;
            }
            this.startDeletingRiskProfile({ rpName });
            try {
                const latestStatus =
                    this.deletingRiskProfileStatus[rpName] || OperationStatus.uninitialized;
                if (latestStatus !== OperationStatus.loading &&
                latestStatus !== OperationStatus.uninitialized) {
                    return;
                }
                await appOf(this).gatewayClient.tprmUi().deleteTprmRiskProfile(rpName, riskProfileId);
                this.finishDeletingRiskProfile({
                    rpName,
                    riskProfileId: payload.riskProfileId,
                });
            } catch (error) {
                this.failDeletingRiskProfile({ rpName });
                await getPersistingErrorActions().showError(error);
            }
        },
        async createRiskProfile(payload: { rpName: string, riskProfile: RiskProfileInput }) {
            const { rpName, riskProfile } = payload;
            const currentStatus =
                this.creatingRiskProfileStatus[rpName] || OperationStatus.uninitialized;
            if (currentStatus === OperationStatus.loading) {
                return;
            }
            this.startCreatingRiskProfile({ rpName });
            try {
                const latestStatus =
                    this.creatingRiskProfileStatus[rpName] || OperationStatus.uninitialized;
                if (latestStatus !== OperationStatus.loading &&
                latestStatus !== OperationStatus.uninitialized) {
                    return;
                }
                const savedRiskProfile = await appOf(this).gatewayClient.tprmUi()
                    .createTprmRiskProfile(rpName, riskProfile);
                this.finishCreatingRiskProfile({ rpName, riskProfile: savedRiskProfile });
            } catch (error) {
                this.failCreatingRiskProfile({ rpName });
                console.error('Failed to create risk profile', error);
                const message = error.error === OperationError.keyConflict
                    ? `A risk profile with the name ${riskProfile.displayName} already exists. Please try a different name.`
                    : 'Something went wrong, please try again or contact support';
                await getSnackbarActions().displaySnackbar({ permanent: false, message });
            }
        },
        async createCriteria(payload: {
            rpName: string;
            riskProfileId: string;
            criteria: CriterionInput[];
        }) {
            const { rpName, riskProfileId, criteria } = payload;
            const currentStatus =
                this.creatingCriteriaStatus[rpName] || OperationStatus.uninitialized;
            if (currentStatus === OperationStatus.loading) {
                return;
            }
            this.startCreatingCriteria({ rpName });
            try {
                const latestStatus =
                    this.creatingCriteriaStatus[rpName] || OperationStatus.uninitialized;
                if (latestStatus !== OperationStatus.loading &&
                latestStatus !== OperationStatus.uninitialized) {
                    return;
                }
                await appOf(this).gatewayClient.tprmUi().createTprmCriteria(rpName, riskProfileId, criteria);
                this.finishCreatingCriteria({ rpName });
            } catch (error) {
                this.failCreatingCriteria({ rpName });
                await getPersistingErrorActions().showError(error);
            }
        },
        async patchCriteria(payload: {
            rpName: string;
            riskProfileId: string;
            patches: any[];
        }) {
            const { rpName, riskProfileId, patches } = payload;
            const currentStatus =
                this.patchingCriteriaStatus[rpName] || OperationStatus.uninitialized;
            if (currentStatus === OperationStatus.loading) {
                return;
            }
            this.startPatchingCriteria({ rpName });
            try {
                const latestStatus =
                    this.patchingCriteriaStatus[rpName] || OperationStatus.uninitialized;
                if (latestStatus !== OperationStatus.loading &&
                latestStatus !== OperationStatus.uninitialized) {
                    return;
                }
                await appOf(this).gatewayClient.tprmUi().patchTprmCriteria(rpName, riskProfileId, patches);
                this.finishPatchingCriteria({ rpName });
            } catch (error) {
                this.failPatchingCriteria({ rpName });
                await getPersistingErrorActions().showError(error);
            }
        },
        async updateRiskProfile(payload: { rpName: string, riskProfile: RiskProfile }) {
            const { rpName, riskProfile } = payload;
            const currentStatus =
                this.updatingRiskProfileStatus[rpName] || OperationStatus.uninitialized;
            if (currentStatus === OperationStatus.loading) {
                return;
            }
            this.startUpdatingRiskProfile({ rpName });
            try {
                const latestStatus =
                    this.updatingRiskProfileStatus[rpName] || OperationStatus.uninitialized;
                if (latestStatus !== OperationStatus.loading &&
                latestStatus !== OperationStatus.uninitialized) {
                    return;
                }
                await appOf(this).gatewayClient.tprmUi().updateTprmRiskProfile(rpName, riskProfile);
                this.finishUpdatingRiskProfile({ rpName });
            } catch (error) {
                this.failUpdatingRiskProfile({ rpName });
                await getPersistingErrorActions().showError(error);
            }
        },
        async loadEffectiveRiskProfiles(payload: { rpName: string }) {
            const { rpName } = payload;
            const currentStatus =
                this.effectiveRiskProfileStatus[rpName] || createEmptyEffectiveRiskProfileStatus();
            if (currentStatus.status === OperationStatus.loading) {
                return;
            }
            this.startLoadingEffectiveRiskProfileStatus({ rpName });
            try {
                const latestStatus =
                    this.effectiveRiskProfileStatus[rpName] || createEmptyRequirementTypesPerCountry();
                if (latestStatus.status !== OperationStatus.loading &&
                latestStatus.status !== OperationStatus.uninitialized) {
                    return;
                }
                const list = await appOf(this).gatewayClient.tprmUi().getTprmEffectiveRiskProfiles(rpName);
                this.finishLoadingEffectiveRiskProfileStatus({ rpName, list });
            } catch (error) {
                this.failLoadingEffectiveRiskProfileStatus({ rpName });
                await getPersistingErrorActions().showError(error);
            }
        },
        async loadRequirementModels(payload: { rpName: string }) {
            const { rpName } = payload;
            const currentStatus =
                this.requirementModelsStatus[rpName] || createEmptyRequirementModelsStatus();
            if (currentStatus.status === OperationStatus.loading) {
                return;
            }
            this.startLoadingRequirementModels({ rpName });
            try {
                const latestStatus =
                    this.requirementModelsStatus[rpName] || createEmptyRequirementModelsStatus();
                if (latestStatus.status !== OperationStatus.loading
                && latestStatus.status !== OperationStatus.uninitialized) {
                    return;
                }
                const list = await appOf(this).gatewayClient.tprmUi().getTprmRequirementTypeModels();
                this.finishLoadingRequirementModels({ rpName, list });
            } catch (error) {
                this.failLoadingRequirementModels({ rpName });
                await getPersistingErrorActions().showError(error);
            }
        },
        clearRiskProfiles(payload: { rpName: string }) {
            this.riskProfiles = omit(this.riskProfiles, [ payload.rpName ]);
        },
        clearRiskProfileCopyDraft(payload: { rpName: string }) {
            this.copyRiskProfileDraft = omit(this.copyRiskProfileDraft, [ payload.rpName ]);
        },
        clearCriterionTemplates(payload: { rpName: string }) {
            this.criterionTemplates = omit(this.criterionTemplates, [ payload.rpName ]);
        },
        clearRequirementTypesPerCountry(payload: { rpName: string }) {
            this.requirementTypesPerCountry = {
                ...this.requirementTypesPerCountry,
                [payload.rpName]: {
                    ...this.requirementTypesPerCountry[payload.rpName],
                    data: {},
                },
            };
        },
        clearCurrentRiskProfiles(payload: { rpName: string }) {
            this.clearCurrentRiskProfile(payload.rpName);
        },
        clearDeletingRiskProfileStatus(payload: { rpName: string }) {
            this.deletingRiskProfileStatus = omit(this.deletingRiskProfileStatus, [ payload.rpName ]);
        },
        clearCreatingRiskProfileStatus(payload: { rpName: string }) {
            this.creatingRiskProfileStatus = omit(this.creatingRiskProfileStatus, [ payload.rpName ]);
        },
        clearCreatingCriteriaStatus(payload: { rpName: string }) {
            this.creatingCriteriaStatus = omit(this.creatingCriteriaStatus, [ payload.rpName ]);
        },
        clearPatchingCriteriaStatus(payload: { rpName: string }) {
            this.patchingCriteriaStatus = omit(this.patchingCriteriaStatus, [ payload.rpName ]);
        },
        clearUpdatingRiskProfileStatus(payload: { rpName: string }) {
            this.updatingRiskProfileStatus = omit(this.updatingRiskProfileStatus, [ payload.rpName ]);
        },
        clearRpCriteria(payload: { rpName: string }) {
            this.criteria = omit(this.criteria, [ payload.rpName ]);
        },
        clearRiskProfileCriteria(payload: { rpName: string, riskProfileId: string }) {
            const { rpName, riskProfileId } = payload;
            this.criteria = {
                ...this.criteria,
                [rpName]: {
                    ...(this.criteria[rpName] as CriteriaStatus),
                    criteria: omit(this.criteria[rpName].criteria, riskProfileId),
                },
            };
        },
        clearRpEntityRiskProfiles(payload: { rpName: string }) {
            this.clearEntityRiskProfilesPerRp(payload.rpName);
        },
        clearEntityRiskProfiles(payload: { rpName: string, entityId: string }) {
            this.clearEntityRiskProfilesPerEntity(payload);
        },
        clearEffectiveRiskProfilesStatus(payload: { rpName: string }) {
            this.clearEffectiveRiskProfileStatus(payload.rpName);
        },
        clearCriterionMessage(payload: { rpName: string }) {
            this.criterionMessage = omit(this.criterionMessage, [ payload.rpName ]);
        },
        clearRequirementModelsStatus(payload: { rpName: string }) {
            this.requirementModelsStatus = omit(this.requirementModelsStatus, [ payload.rpName ]);
        },
    },
});
